/**
 * キーボードで進んでいる位置に枠を追わせる
 *
 * @description 各要素に outline を掛けると、丸や重なりのある部品で形が合わない。
 *              代わりに枠を1枚だけ持ち、いま :focus-visible の要素へ寄せる。
 *              枠の見た目は _common_a11y.scss が持ち、ここは位置と表示だけを決める。
 *
 *              囲み方を変えたい要素は data-a11y-selection-class に型を書く。
 *              例：<button data-a11y-selection-class="type-no-pad">
 */

/** 枠そのもの。.wrapper の外にあるので非同期遷移でも作り直されない */
const SELECTOR = '.a11y-selection';

/** いま追っている要素 */
let target: HTMLElement | null = null;

/** 位置を追う rAF。追っていない間は 0 */
let frame = 0;

/** 解除する手続き */
let detach: (() => void) | null = null;

/**
 * 直前の操作が鍵盤だったか
 *
 * @description :focus-visible の当たり方はエンジンごとに違い、WebKitは指で触った
 *              あとのフォーカスにも当ててくる。とくに引き出しの開け閉めのように
 *              JSが自分でフォーカスを移す場面では、触っただけなのに枠が出る。
 *              最後に触れたのが指か鍵盤かを自分で覚えて、鍵盤のときだけ囲う
 */
let byKeyboard = false;

/**
 * 枠を返す
 *
 * @returns 枠。無ければ null
 */
const outline = (): HTMLElement | null => document.querySelector<HTMLElement>(SELECTOR);

/**
 * 追うのをやめて枠を伏せる
 *
 * @returns {void}
 */
const stopTracking = (): void => {
    target = null;

    if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
    }

    const el = outline();

    if (!el) return;

    el.style.opacity = '0';
    delete el.dataset.a11ySelectionClass;
};

/**
 * 枠を今の位置と大きさへ寄せ、次のフレームも同じことをする
 *
 * @description スクロールや慣性で動いても離れないよう、毎フレーム測り直す。
 *              追う相手が画面から外れたら（差し替えなどで箱が消えたら）追うのをやめる。
 * @returns {void}
 */
const follow = (): void => {
    if (!target) return;

    const el = outline();

    if (!el) return;

    // 遷移で中身ごと入れ替わると、掴んだままの要素は箱を持たなくなる
    if (!target.isConnected) {
        stopTracking();
        return;
    }

    const rect = target.getBoundingClientRect();

    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.transform = `translate(${rect.left}px, ${rect.top}px)`;

    frame = requestAnimationFrame(follow);
};

/**
 * 追いはじめる
 *
 * @param el 追う相手
 * @returns {void}
 */
const startTracking = (el: HTMLElement): void => {
    const box = outline();

    if (!box) return;

    target = el;

    const type = el.dataset.a11ySelectionClass;

    if (type) {
        box.dataset.a11ySelectionClass = type;
    } else {
        delete box.dataset.a11ySelectionClass;
    }

    box.style.opacity = '1';

    if (frame) cancelAnimationFrame(frame);

    frame = requestAnimationFrame(follow);
};

/**
 * 監視をやめる。非同期遷移で中身ごと作り直すときに使う
 *
 * @returns {void}
 */
const destroyFocusOutline = (): void => {
    stopTracking();
    detach?.();
    detach = null;
};

/**
 * キーボードのフォーカスを追いはじめる
 *
 * @returns {void}
 */
const initFocusOutline = (): void => {
    destroyFocusOutline();

    if (!outline()) return;

    const onFocusIn = (e: FocusEvent): void => {
        const el = e.target;

        if (!(el instanceof HTMLElement)) return;

        // 非同期遷移のあと body へフォーカスが移される。Tabで止まらない相手は囲まない
        if (el.getAttribute('tabindex') === '-1') {
            stopTracking();
            return;
        }

        // 指やマウスで押しただけのときは出さない。鍵盤で進んだときだけ囲う
        if (byKeyboard && el.matches(':focus-visible')) {
            startTracking(el);
        }
    };

    const onFocusOut = (): void => stopTracking();

    // 触った先がフォーカスを受けない相手だと focusout が来ないので、ここでも伏せる
    const onPointerDown = (): void => {
        byKeyboard = false;
        stopTracking();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
        // ショートカットの組み合わせは移動ではないので、鍵盤で進んだとはみなさない
        if (!e.metaKey && !e.ctrlKey && !e.altKey) byKeyboard = true;

        if (e.key === 'Escape') stopTracking();
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    detach = () => {
        document.removeEventListener('focusin', onFocusIn);
        document.removeEventListener('focusout', onFocusOut);
        document.removeEventListener('pointerdown', onPointerDown, true);
        document.removeEventListener('keydown', onKeyDown);
    };
};

export { initFocusOutline, destroyFocusOutline, stopTracking as stopFocusOutline };

document.addEventListener('DOMContentLoaded', initFocusOutline);
